import { DEFAULT_LOCALE, formatDate } from '@/lib/format/date'
import type { StyleGuide, StyleGuideSample } from '@/types/brand'

/** The free-text sections of the Style Guide, in the server's normative document order. */
export const STYLE_GUIDE_PROSE_SECTIONS = [
  'voiceAndPersonality',
  'formalityAndAddress',
  'referenceGrammarStandard',
  'numbersDatesCurrency',
  'punctuationAndQuotes',
  'capitalization',
  'inclusiveLanguage',
] as const

export type StyleGuideProseSection = (typeof STYLE_GUIDE_PROSE_SECTIONS)[number]

/** The heading each prose section carries, in markdown and in the editor alike. */
const STYLE_GUIDE_PROSE_HEADINGS: Record<StyleGuideProseSection, string> = {
  voiceAndPersonality: 'Voice and personality',
  formalityAndAddress: 'Formality and address',
  referenceGrammarStandard: 'Reference grammar standard',
  numbersDatesCurrency: 'Numbers, dates and currency',
  punctuationAndQuotes: 'Punctuation and quotes',
  capitalization: 'Capitalization',
  inclusiveLanguage: 'Inclusive language',
}

const TERMS_HEADING = 'Brand and product terms'
const PREFERENCES_HEADING = 'Preferred and avoided words'
const SAMPLES_HEADING = 'Sample copy'

function section(heading: string, content: string): string | null {
  const trimmed = content.trim()
  return trimmed === '' ? null : `## ${heading}\n\n${trimmed}`
}

function quote(body: string): string {
  return body
    .trim()
    .split(/\r?\n/)
    .map((line) => (line.trim() === '' ? '>' : `> ${line}`))
    .join('\n')
}

function attribution(sample: StyleGuideSample, locale: string): string | null {
  const published = formatDate(sample.publishedAt, 'longDate', locale)
  if (sample.sourceUrl === null) return published === '' ? null : published
  return `[${published === '' ? 'View the original post' : published}](${sample.sourceUrl})`
}

function sampleToMarkdown(sample: StyleGuideSample, locale: string): string {
  const credit = attribution(sample, locale)
  return credit === null ? quote(sample.body) : `${quote(sample.body)}\n\n${credit}`
}

/** Serialises a Style Guide to the markdown the Documents library reads; a section the extraction could not fill is left out; `locale` renders sample-copy dates. */
export function styleGuideToMarkdown(guide: StyleGuide, locale: string = DEFAULT_LOCALE): string {
  const blocks: (string | null)[] = [
    ...STYLE_GUIDE_PROSE_SECTIONS.map((key) =>
      section(STYLE_GUIDE_PROSE_HEADINGS[key], guide[key]),
    ),
    section(
      TERMS_HEADING,
      guide.brandAndProductTerms.map((t) => `- **${t.term}** - ${t.correctForm}`).join('\n'),
    ),
    section(
      PREFERENCES_HEADING,
      guide.preferredAndAvoidedWords
        .map((p) => `- **${p.avoid}** - use "${p.useInstead}"`)
        .join('\n'),
    ),
    section(
      SAMPLES_HEADING,
      guide.sampleCopy.map((sample) => sampleToMarkdown(sample, locale)).join('\n\n'),
    ),
  ]

  return blocks.filter((block): block is string => block !== null).join('\n\n')
}
